import { temarioNodeHasDocumentFiles } from "./campusVisibleContent";
import {
  contenidoOrdenFromItems,
  listContenidoItems,
  normalizeContenidoOrdenEntry,
  testOrdenKey,
} from "./contenidoOrden";
import { courseTestTitle } from "./listLinkableCourseTests";
import { normalizeCourseTestsToArray } from "./normalizeCourseShape";
import { normalizeTabKey, normalizeTestPestanasForTab, testMatchesTab } from "./testMatchesTab";

/**
 * Membership catalog from the student tests/estructura payload.
 * Block name is the same tab key Admin > Test stores in test.pestanas.
 *
 * @param {Array<{ nombre?: string, temas?: Array<{ nombre?: string }> }> | null | undefined} estructura
 * @returns {object[]}
 */
export function courseTestsFromTestEstructura(estructura) {
  if (!Array.isArray(estructura)) {
    return [];
  }
  /** @type {object[]} */
  const tests = [];
  estructura.forEach((block) => {
    const tab = typeof block?.nombre === "string" ? block.nombre.trim() : "";
    if (!tab) {
      return;
    }
    const temas = Array.isArray(block.temas) ? block.temas : [];
    temas.forEach((tema) => {
      const titulo = typeof tema?.nombre === "string" ? tema.nombre.trim() : "";
      if (!titulo) {
        return;
      }
      tests.push({ titulo, pestanas: [tab] });
    });
  });
  return tests;
}

/**
 * Prefer the course tests[] array; otherwise the published estructura map.
 *
 * @param {object[] | object | null | undefined} courseTests
 * @param {Array<{ nombre?: string, temas?: Array<{ nombre?: string }> }> | null | undefined} testEstructura
 * @returns {object[]}
 */
export function resolveCourseTestsCatalog(courseTests, testEstructura) {
  const fromCourse = normalizeCourseTestsToArray(courseTests);
  if (fromCourse.length > 0) {
    return fromCourse;
  }
  return courseTestsFromTestEstructura(testEstructura);
}

/**
 * @param {{ titulo?: string, bloque?: string } | null | undefined} testRef
 * @param {object[] | null | undefined} courseTests
 * @param {string} tabLabel
 * @param {string[] | null | undefined} [courseTabs]
 * @returns {boolean}
 */
export function testRefBelongsToTab(testRef, courseTests, tabLabel, courseTabs) {
  const tab = typeof tabLabel === "string" ? tabLabel.trim() : "";
  if (!tab) {
    return false;
  }
  const title = typeof testRef?.titulo === "string" ? testRef.titulo.trim() : "";
  const catalog = Array.isArray(courseTests) ? courseTests : [];
  const titleMatches = catalog.filter((test) => courseTestTitle(test) === title);
  if (titleMatches.some((test) => testMatchesTab(test, tab))) {
    return true;
  }
  if (titleMatches.length > 0) {
    const publishedTabs = (Array.isArray(courseTabs) ? courseTabs : [])
      .map((item) => normalizeTabKey(item))
      .filter(Boolean);
    const assignedToPublishedTab = publishedTabs.length > 0
      && titleMatches.some((test) => (
        normalizeTestPestanasForTab(test).some((label) => publishedTabs.includes(label))
      ));
    if (assignedToPublishedTab) {
      return false;
    }
    return true;
  }
  if (catalog.length === 0) {
    return true;
  }
  const bloque = typeof testRef?.bloque === "string" ? testRef.bloque.trim() : "";
  if (!bloque) {
    return false;
  }
  return normalizeTabKey(bloque) === normalizeTabKey(tab);
}

/**
 * Files followed by tests is the fallback listContenidoItems already produces
 * when the block has no saved mixed sequence. Normalize writes that fallback
 * into contenidoOrden, so it must not override tab membership.
 *
 * @param {import("./temarioTree").TemarioNodo | null | undefined} node
 * @returns {boolean}
 */
function contenidoOrdenIsImplicitFileThenTests(node) {
  const actual = Array.isArray(node?.contenidoOrden)
    ? node.contenidoOrden
      .map((entry) => normalizeContenidoOrdenEntry(entry))
      .filter((entry) => entry !== null)
    : [];
  if (actual.length === 0) {
    return true;
  }
  const implicit = contenidoOrdenFromItems(listContenidoItems({
    archivos: node?.archivos,
    testRefs: node?.testRefs,
  }));
  if (actual.length !== implicit.length) {
    return false;
  }
  return actual.every((entry, index) => {
    const other = implicit[index];
    if (!other || entry.kind !== other.kind) {
      return false;
    }
    if (entry.kind === "test") {
      return entry.titulo === other.titulo;
    }
    return entry.nombre === other.nombre;
  });
}

/**
 * True when the director saved this test inside a mixed block sequence.
 * That sequence wins over catalog tab membership for the student view.
 *
 * @param {import("./temarioTree").TemarioNodo | null | undefined} node
 * @param {{ titulo?: string } | null | undefined} testRef
 * @returns {boolean}
 */
function explicitContenidoOrdenHasTest(node, testRef) {
  const titulo = testOrdenKey(testRef);
  const orden = Array.isArray(node?.contenidoOrden) ? node.contenidoOrden : [];
  if (!titulo || orden.length === 0 || contenidoOrdenIsImplicitFileThenTests(node)) {
    return false;
  }
  return orden.some((entry) => {
    const normalized = normalizeContenidoOrdenEntry(entry);
    return normalized?.kind === "test" && normalized.titulo === titulo;
  });
}

/**
 * Display clone: keeps files and children, drops testRefs that belong to another tab.
 * A saved mixed sequence (not the files-then-tests fallback) keeps its tests.
 * Does not write a new persistence model.
 *
 * @param {import("./temarioTree").TemarioNodo[] | null | undefined} nodes
 * @param {object[] | null | undefined} courseTests
 * @param {string} tabLabel
 * @param {string[] | null | undefined} [courseTabs]
 * @returns {import("./temarioTree").TemarioNodo[]}
 */
export function filterTemarioNodesTestsForTab(nodes, courseTests, tabLabel, courseTabs) {
  if (!Array.isArray(nodes)) {
    return [];
  }
  const catalog = Array.isArray(courseTests) ? courseTests : [];
  if (catalog.length === 0) {
    return nodes;
  }
  return nodes.map((node) => filterTemarioNodeTestsForTab(node, courseTests, tabLabel, courseTabs));
}

/**
 * @param {import("./temarioTree").TemarioNodo} node
 * @param {object[] | null | undefined} courseTests
 * @param {string} tabLabel
 * @returns {import("./temarioTree").TemarioNodo}
 */
function filterTemarioNodeTestsForTab(node, courseTests, tabLabel, courseTabs) {
  if (!node || typeof node !== "object") {
    return node;
  }
  const items = listContenidoItems(node).filter((item) => (
    item.kind !== "test"
    || testRefBelongsToTab(item.testRef, courseTests, tabLabel, courseTabs)
    || explicitContenidoOrdenHasTest(node, item.testRef)
  ));
  return {
    ...node,
    testRefs: items.filter((item) => item.kind === "test").map((item) => item.testRef),
    contenidoOrden: contenidoOrdenFromItems(items),
    hijos: Array.isArray(node.hijos)
      ? node.hijos.map((child) => filterTemarioNodeTestsForTab(child, courseTests, tabLabel, courseTabs))
      : [],
  };
}

/**
 * Catalog tests for one tab, in the same array order Admin uses for Subir/Bajar.
 *
 * @param {object[] | null | undefined} courseTests
 * @param {string} tabLabel
 * @returns {object[]}
 */
export function listCourseTestsForTab(courseTests, tabLabel) {
  const tab = typeof tabLabel === "string" ? tabLabel.trim() : "";
  if (!tab) {
    return [];
  }
  return (Array.isArray(courseTests) ? courseTests : []).filter((test) => (
    test && typeof test === "object" && testMatchesTab(test, tab)
  ));
}

function isStudentTestBlockNode(node) {
  return typeof node?.id === "string" && node.id.startsWith("test-block-");
}

function topicNodeFromCatalogTest(tabLabel, title) {
  return {
    id: `test-topic-${tabLabel}-${title}`,
    nombre: title,
    archivos: [],
    testRefs: [{ titulo: title, bloque: tabLabel, tema: title }],
    hijos: [],
  };
}

function collectDisplayedTestTitles(nodes, titles) {
  if (!Array.isArray(nodes)) {
    return;
  }
  nodes.forEach((node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    const refs = Array.isArray(node.testRefs) ? node.testRefs : [];
    refs.forEach((ref) => {
      const title = typeof ref?.titulo === "string" ? ref.titulo.trim() : "";
      if (title) {
        titles.add(title);
      }
    });
    if (isStudentTestBlockNode(node) && Array.isArray(node.hijos)) {
      node.hijos.forEach((child) => {
        const name = typeof child?.nombre === "string" ? child.nombre.trim() : "";
        if (name) {
          titles.add(name);
        }
      });
    }
    collectDisplayedTestTitles(Array.isArray(node.hijos) ? node.hijos : [], titles);
  });
}

function catalogTitleOrder(courseTests, tabLabel) {
  /** @type {Map<string, number>} */
  const order = new Map();
  listCourseTestsForTab(courseTests, tabLabel).forEach((test, index) => {
    const title = courseTestTitle(test);
    if (title && !order.has(title)) {
      order.set(title, index);
    }
  });
  return order;
}

function sortStudentTestBlockTopics(nodes, order) {
  if (!Array.isArray(nodes)) {
    return [];
  }
  return nodes.map((node) => {
    if (!node || typeof node !== "object") {
      return node;
    }
    const children = Array.isArray(node.hijos) ? node.hijos : [];
    if (isStudentTestBlockNode(node)) {
      const sorted = [...children].sort((left, right) => {
        const leftName = typeof left?.nombre === "string" ? left.nombre.trim() : "";
        const rightName = typeof right?.nombre === "string" ? right.nombre.trim() : "";
        const leftIndex = order.has(leftName) ? order.get(leftName) : Number.MAX_SAFE_INTEGER;
        const rightIndex = order.has(rightName) ? order.get(rightName) : Number.MAX_SAFE_INTEGER;
        return leftIndex - rightIndex;
      });
      return { ...node, hijos: sorted };
    }
    return { ...node, hijos: sortStudentTestBlockTopics(children, order) };
  });
}

/**
 * Display-only: keep the current campus tree, fill missing tab tests on the
 * existing test block, and sort that block with the persisted Admin order.
 *
 * @param {import("./temarioTree").TemarioNodo[] | null | undefined} nodes
 * @param {object[] | null | undefined} courseTests
 * @param {string} tabLabel
 * @param {string[] | null | undefined} [courseTabs]
 * @returns {import("./temarioTree").TemarioNodo[]}
 */
export function applyCatalogTestsToTabTree(nodes, courseTests, tabLabel, courseTabs) {
  const filtered = filterTemarioNodesTestsForTab(nodes, courseTests, tabLabel, courseTabs);
  const wanted = listCourseTestsForTab(courseTests, tabLabel);
  if (wanted.length === 0) {
    return filtered;
  }

  const visible = new Set();
  collectDisplayedTestTitles(filtered, visible);
  const missing = wanted.filter((test) => {
    const title = courseTestTitle(test);
    return title.length > 0 && !visible.has(title);
  });

  let next = filtered;
  if (missing.length > 0) {
    const blockIndex = next.findIndex((node) => isStudentTestBlockNode(node));
    if (blockIndex >= 0) {
      const block = next[blockIndex];
      next = [...next];
      next[blockIndex] = {
        ...block,
        hijos: [
          ...(Array.isArray(block.hijos) ? block.hijos : []),
          ...missing.map((test) => topicNodeFromCatalogTest(tabLabel, courseTestTitle(test))),
        ],
      };
    } else if (!next.some((node) => temarioNodeHasDocumentFiles(node))) {
      next = [
        ...next,
        {
          id: `test-block-${tabLabel}`,
          nombre: tabLabel,
          archivos: [],
          testRefs: [],
          hijos: wanted.map((test) => topicNodeFromCatalogTest(tabLabel, courseTestTitle(test))),
        },
      ];
    }
  }

  return sortStudentTestBlockTopics(next, catalogTitleOrder(courseTests, tabLabel));
}
